"use client";

import React from "react";
import { motion, type Variants } from "motion/react";
import { MapPin, Mail, Clock, ArrowUpRight, Loader2 } from "lucide-react";
import { Toaster } from "sonner";
import { Controller } from "react-hook-form";

import { FormField } from "./FormField";
import { FileUpload } from "./FileUpload";
import { useDynamicForm } from "@/hooks/useDynamicForm";   // 👈 1. swap the import
import type { ContactFormValues } from "@/types/contact";
import {
  SERVICE_OPTIONS,
  PRODUCT_CATEGORY_OPTIONS,
  COUNTRY_OPTIONS,
} from "@/types/contact";
import s from "./ContactSection.module.css";

// ─── Motion ────────────────────────────────────────────────────────────────
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.12 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
};

const fromLeft: Variants = {
  hidden: { opacity: 0, x: -32 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.75, ease: EASE } },
};

const fromRight: Variants = {
  hidden: { opacity: 0, y: 36 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

const EMAILS = [
  "priority@thequalitycollection.com",
  "sales@thequalitycollection.com",
  "care@thequalitycollection.com",
];

// ─── Info row ──────────────────────────────────────────────────────────────
function InfoRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <motion.div variants={item} className={s.infoItem}>
      <span className={s.infoIcon}>{icon}</span>
      <div>
        <p className={s.infoLabel}>{label}</p>
        {children}
      </div>
    </motion.div>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────
export function ContactSection() {
  const {
    register,
    handleSubmit,
    control,
    errors,
    isSubmitting,
    selectedFile,
    handleFileSelect,
    onSubmit,
    onError,
  } = useContactForm();

  const cls = (base: string, hasError?: unknown) =>
    `${base} ${hasError ? s.controlError : ""}`.trim();

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#FFFFFF",
            border: "1px solid rgba(169,136,79,0.28)",
            color: "#241E18",
            borderRadius: "14px",
            boxShadow: "0 18px 40px -20px rgba(23,19,15,0.35)",
          },
        }}
      />

      <section className={s.section} aria-labelledby="contact-heading">
        {/* Ambient background */}
        <div className={s.bgLayer} aria-hidden="true">
          <div className={s.bgGlowA} />
          <div className={s.bgGlowB} />
          <div className={s.bgGrid} />
          <div className={s.bgGrain} />
        </div>

        <div className={s.shell}>
          <div className={s.grid}>
            {/* ══════════ LEFT ══════════ */}
            <motion.div
              className={s.left}
              variants={fromLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-90px" }}
            >
              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.div variants={item} className={s.badge}>
                  <span className={s.badgeMark}>TQC</span>
                  Get in touch
                </motion.div>

                <motion.h2 variants={item} id="contact-heading" className={s.heading}>
                  Let&apos;s build your
                  <span className={s.headingAccent}>next collection</span>
                </motion.h2>

                <motion.div variants={item} className={s.rule} aria-hidden="true">
                  <span className={s.ruleLine} />
                  <span className={s.ruleDot} />
                </motion.div>

                <motion.p variants={item} className={s.lede}>
                  From bespoke manufacturing and OEM production to private label,
                  product development and global export — our atelier team is
                  ready to craft something exceptional with you.
                </motion.p>

                <motion.div
                  className={s.infoList}
                  variants={stagger}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <InfoRow icon={<MapPin size={18} strokeWidth={1.6} />} label="Atelier">
                    <address className={s.infoBody}>
                      The Quality Collection Private Limited
                      <br />
                      Near Behind Petrol Pump, Tajpur Mafi
                      <br />
                      Dalpatpur Road, Moradabad
                      <br />
                      Uttar Pradesh 244001, India
                    </address>
                  </InfoRow>

                  <InfoRow icon={<Mail size={18} strokeWidth={1.6} />} label="Correspondence">
                    <div className={s.mailStack}>
                      {EMAILS.map((email) => (
                        <a key={email} href={`mailto:${email}`} className={s.mailLink}>
                          {email}
                        </a>
                      ))}
                    </div>
                  </InfoRow>

                  <InfoRow icon={<Clock size={18} strokeWidth={1.6} />} label="Hours">
                    <p className={s.infoBody}>
                      Monday – Saturday
                      <br />
                      9:00 AM – 6:30 PM IST
                    </p>
                  </InfoRow>
                </motion.div>
              </motion.div>
            </motion.div>

            {/* ══════════ RIGHT ══════════ */}
            <motion.div
              className={s.card}
              variants={fromRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-90px" }}
            >
              <span className={s.cardTopline} aria-hidden="true" />
              <span className={s.cardCorner} aria-hidden="true" />

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <motion.div variants={item} className={s.cardHeader}>
                  <span className={s.cardKicker}>Enquiry</span>
                  <h3 className={s.cardTitle}>Request a quotation</h3>
                </motion.div>

                <form
                  className={s.form}
                  onSubmit={handleSubmit(onSubmit, onError)}
                  noValidate
                  aria-label="Quotation request form"
                >
                  {/* Row 1 */}
                  <motion.div variants={item} className={s.row}>
                    <FormField id="fullName" label="Full Name" required error={errors.fullName?.message}>
                      <input
                        id="fullName"
                        type="text"
                        placeholder="John Doe"
                        autoComplete="name"
                        className={cls(s.control, errors.fullName)}
                        aria-invalid={!!errors.fullName}
                        aria-describedby={errors.fullName ? "fullName-error" : undefined}
                        {...register("fullName", {
                          required: "This field is required.",
                          minLength: { value: 3, message: "At least 3 characters." },
                          maxLength: { value: 50, message: "Maximum 50 characters." },
                        })}
                      />
                    </FormField>

                    <FormField id="companyName" label="Company" required error={errors.companyName?.message}>
                      <input
                        id="companyName"
                        type="text"
                        placeholder="Your Company Ltd."
                        autoComplete="organization"
                        className={cls(s.control, errors.companyName)}
                        aria-invalid={!!errors.companyName}
                        aria-describedby={errors.companyName ? "companyName-error" : undefined}
                        {...register("companyName", { required: "This field is required." })}
                      />
                    </FormField>
                  </motion.div>

                  {/* Row 2 */}
                  <motion.div variants={item} className={s.row}>
                    <FormField id="businessEmail" label="Business Email" required error={errors.businessEmail?.message}>
                      <input
                        id="businessEmail"
                        type="email"
                        placeholder="you@company.com"
                        autoComplete="email"
                        className={cls(s.control, errors.businessEmail)}
                        aria-invalid={!!errors.businessEmail}
                        aria-describedby={errors.businessEmail ? "businessEmail-error" : undefined}
                        {...register("businessEmail", {
                          required: "This field is required.",
                          pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: "Enter a valid email address.",
                          },
                        })}
                      />
                    </FormField>

                    <FormField id="phoneNumber" label="Phone" required error={errors.phoneNumber?.message}>
                      <input
                        id="phoneNumber"
                        type="tel"
                        placeholder="+1 800 000 0000"
                        autoComplete="tel"
                        className={cls(s.control, errors.phoneNumber)}
                        aria-invalid={!!errors.phoneNumber}
                        aria-describedby={errors.phoneNumber ? "phoneNumber-error" : undefined}
                        {...register("phoneNumber", {
                          required: "This field is required.",
                          validate: (value: string) => {
                            const digits = value.replace(/[^\d]/g, "");
                            if (digits.length < 8) return "Minimum 8 digits required.";
                            if (digits.length > 15) return "Maximum 15 digits allowed.";
                            return true;
                          },
                        })}
                      />
                    </FormField>
                  </motion.div>

                  {/* Row 3 */}
                  <motion.div variants={item} className={s.row}>
                    <FormField id="country" label="Country" required error={errors.country?.message}>
                      <select
                        id="country"
                        className={cls(`${s.control} ${s.select}`, errors.country)}
                        aria-invalid={!!errors.country}
                        aria-describedby={errors.country ? "country-error" : undefined}
                        defaultValue=""
                        {...register("country", { required: "This field is required." })}
                      >
                        {COUNTRY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value} disabled={o.value === ""}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </FormField>

                    <FormField id="serviceRequired" label="Service" required error={errors.serviceRequired?.message}>
                      <select
                        id="serviceRequired"
                        className={cls(`${s.control} ${s.select}`, errors.serviceRequired)}
                        aria-invalid={!!errors.serviceRequired}
                        aria-describedby={errors.serviceRequired ? "serviceRequired-error" : undefined}
                        defaultValue=""
                        {...register("serviceRequired", { required: "This field is required." })}
                      >
                        {SERVICE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value} disabled={o.value === ""}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </FormField>
                  </motion.div>

                  {/* Row 4 */}
                  <motion.div variants={item} className={s.row}>
                    <FormField id="productCategory" label="Category" required error={errors.productCategory?.message}>
                      <select
                        id="productCategory"
                        className={cls(`${s.control} ${s.select}`, errors.productCategory)}
                        aria-invalid={!!errors.productCategory}
                        aria-describedby={errors.productCategory ? "productCategory-error" : undefined}
                        defaultValue=""
                        {...register("productCategory", { required: "This field is required." })}
                      >
                        {PRODUCT_CATEGORY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value} disabled={o.value === ""}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </FormField>

                    <FormField
                      id="estimatedQuantity"
                      label="Quantity"
                      optional
                      error={errors.estimatedQuantity?.message}
                    >
                      <input
                        id="estimatedQuantity"
                        type="text"
                        placeholder="e.g. 500 units"
                        className={cls(s.control, errors.estimatedQuantity)}
                        {...register("estimatedQuantity")}
                      />
                    </FormField>
                  </motion.div>

                  {/* Message */}
                  <motion.div variants={item}>
                    <FormField id="message" label="Project Brief" required error={errors.message?.message}>
                      <textarea
                        id="message"
                        rows={4}
                        placeholder="Tell us about materials, finishes, timelines and any specific requirements…"
                        className={cls(`${s.control} ${s.textarea}`, errors.message)}
                        aria-invalid={!!errors.message}
                        aria-describedby={errors.message ? "message-error" : undefined}
                        {...register("message", {
                          required: "This field is required.",
                          minLength: { value: 30, message: "Please write at least 30 characters." },
                          maxLength: { value: 1000, message: "Maximum 1000 characters." },
                        })}
                      />
                    </FormField>
                  </motion.div>

                  {/* File */}
                  <motion.div variants={item}>
                    <Controller
                      name="referenceFile"
                      control={control}
                      render={() => (
                        <FileUpload onFileSelect={handleFileSelect} selectedFile={selectedFile} />
                      )}
                    />
                  </motion.div>

                  {/* Consent */}
                  <motion.div variants={item} className={s.consent}>
                    <input
                      id="privacyPolicy"
                      type="checkbox"
                      className={s.checkInput}
                      aria-invalid={!!errors.privacyPolicy}
                      aria-describedby={errors.privacyPolicy ? "privacyPolicy-error" : undefined}
                      {...register("privacyPolicy", {
                        required: "Please accept the privacy policy to continue.",
                      })}
                    />
                    <label
                      htmlFor="privacyPolicy"
                      className={`${s.checkBox} ${errors.privacyPolicy ? s.checkBoxError : ""}`}
                      aria-hidden="true"
                    />
                    <div>
                      <label htmlFor="privacyPolicy" className={s.consentText}>
                        I agree to the{" "}
                        <a href="/privacy-policy" className={s.consentLink}>
                          Privacy Policy
                        </a>{" "}
                        and consent to The Quality Collection Private Limited processing my enquiry.
                      </label>
                      {errors.privacyPolicy && (
                        <p className={s.errorMsg} id="privacyPolicy-error" role="alert">
                          {errors.privacyPolicy.message as string}
                        </p>
                      )}
                    </div>
                  </motion.div>

                  {/* Submit */}
                  <motion.div variants={item} className={s.submitWrap}>
                    <button type="submit" className={s.submit} disabled={isSubmitting}>
                      <span className={s.submitSheen} aria-hidden="true" />
                      {isSubmitting ? (
                        <>
                          <Loader2 size={17} className={s.spin} aria-hidden="true" />
                          <span className={s.submitLabel}>Submitting…</span>
                        </>
                      ) : (
                        <>
                          <span className={s.submitLabel}>Request Quotation</span>
                          <span className={s.submitIcon} aria-hidden="true">
                            <ArrowUpRight size={15} strokeWidth={2.4} />
                          </span>
                        </>
                      )}
                    </button>

                    <p className={s.assurance}>
                      <span className={s.assuranceDot} aria-hidden="true" />
                      Typical response within 24 business hours
                    </p>
                  </motion.div>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
}